import type { ChangeEvent, ReactNode } from 'react';

interface Option<T extends string> {
  value: T;
  label: string;
  /** Optional title attribute for hover hint on the option. */
  hint?: string;
}

interface Props<T extends string> {
  /** Short label rendered before the dropdown (e.g. "Users", "Bucket"). */
  label: ReactNode;
  value: T;
  options: readonly Option<T>[];
  onChange: (next: T) => void;
  /** Optional aria-label override; defaults to the visible label text. */
  ariaLabel?: string;
}

/**
 * Compact dropdown filter styled to match the chip aesthetic. Uses a
 * native `<select>` (free keyboard nav + accessibility) with a custom
 * border, font, and chevron via CSS.
 */
export function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  ariaLabel,
}: Props<T>): JSX.Element {
  const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    onChange(event.target.value as T);
  };

  return (
    <label className="filter-control">
      <span className="filter-control-label">{label}</span>
      <span className="filter-select-wrap">
        <select
          className="filter-select"
          value={value}
          onChange={handleChange}
          aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} title={opt.hint}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="filter-select-arrow" aria-hidden="true">
          ▾
        </span>
      </span>
    </label>
  );
}
